import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios, { apiAxiosUpload } from "helpers/apiAxios";
import { objectSerializer } from "helpers/utils";
import { toast } from "react-toastify";
import { handleAsyncThunkError } from "redux/reduxHelpers";

import {
  setNestedPageHelper,
  setNestedPageSizeHelper,
  setNestedParamsHelper
} from "helpers/reducerHelpers";

const changeOrderRevisionUrl = (projectId, buildingId, changeOrderId) => (
  `/crm/projects/${projectId}/buildings/${buildingId}/change_orders/${changeOrderId}/revisions`
)

export const getChangeOrderRevisions = createAsyncThunk(
  "ChangeOrderRevision/getChangeOrderRevisions",
  async ({ projectId, buildingId, changeOrderId, params }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.get(url, { params })
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to load revisions'))
    }
  }
)

export const getChangeOrderRevision = createAsyncThunk(
  "ChangeOrderRevision/getChangeOrderRevision",
  async ({ projectId, buildingId, changeOrderId, revisionId }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.get(`${url}/${revisionId}`)
      return response.data.data.attributes
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to load revision'))
    }
  }
)

export const createChangeOrderRevision = createAsyncThunk(
  "ChangeOrderRevision/createChangeOrderRevision",
  async ({ projectId, buildingId, changeOrderId, data }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.post(url, data)
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to create revision'))
    }
  }
)

export const updateChangeOrderRevision = createAsyncThunk(
  "ChangeOrderRevision/updateChangeOrderRevision",
  async ({ projectId, buildingId, changeOrderId, revisionId, data }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.put(`${url}/${revisionId}`, data)
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to update revision'))
    }
  }
)

export const deleteChangeOrderRevision = createAsyncThunk(
  "ChangeOrderRevision/deleteChangeOrderRevision",
  async ({ projectId, buildingId, changeOrderId, revisionId }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.delete(`${url}/${revisionId}`)
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to delete revision'))
    }
  }
)

export const sendRevisionToCSD = createAsyncThunk(
  "ChangeOrderRevision/sendRevisionToCSD",
  async ({ projectId, buildingId, changeOrderId, revisionId, data }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.post(`${url}/${revisionId}/send_to_csd`, data)
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to send revision to CSD'))
    }
  }
)

// data: { submited_type: 'accept' | 'reject', note }
export const confirmRevision = createAsyncThunk(
  "ChangeOrderRevision/confirmRevision",
  async ({ projectId, buildingId, changeOrderId, revisionId, data }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.post(`${url}/${revisionId}/confirm`, data)
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to confirm revision'))
    }
  }
)

// data: { documents: [files], mode: 'new' | 'replace', target_attachment_id }
export const uploadRevisionDocument = createAsyncThunk(
  "ChangeOrderRevision/uploadRevisionDocument",
  async ({ projectId, buildingId, changeOrderId, revisionId, data }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxiosUpload.post(`${url}/${revisionId}/upload_document`, data)
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to upload document'))
    }
  }
)

export const removeRevisionDocument = createAsyncThunk(
  "ChangeOrderRevision/removeRevisionDocument",
  async ({ projectId, buildingId, changeOrderId, revisionId, data }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.post(`${url}/${revisionId}/remove_document`, data)
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to remove document'))
    }
  }
)

// data: { target_attachment_id } — mark an existing CO# PPD/RAD document for
// deletion on accept (or undo it).
export const markRevisionDocumentRemoval = createAsyncThunk(
  "ChangeOrderRevision/markRevisionDocumentRemoval",
  async ({ projectId, buildingId, changeOrderId, revisionId, data }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.post(`${url}/${revisionId}/mark_document_removal`, data)
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to mark document for removal'))
    }
  }
)

export const unmarkRevisionDocumentRemoval = createAsyncThunk(
  "ChangeOrderRevision/unmarkRevisionDocumentRemoval",
  async ({ projectId, buildingId, changeOrderId, revisionId, data }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.post(`${url}/${revisionId}/unmark_document_removal`, data)
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to undo document removal'))
    }
  }
)

// data: { document_id, mode: 'new' | 'replace', target_attachment_id }
export const updateRevisionDocumentAction = createAsyncThunk(
  "ChangeOrderRevision/updateRevisionDocumentAction",
  async ({ projectId, buildingId, changeOrderId, revisionId, data }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.post(`${url}/${revisionId}/update_document_action`, data)
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to update document action'))
    }
  }
)

export const cloneRevisionCOForm = createAsyncThunk(
  "ChangeOrderRevision/cloneRevisionCOForm",
  async ({ projectId, buildingId, changeOrderId, revisionId }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.post(`${url}/${revisionId}/clone_change_order_form`)
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to clone CO form'))
    }
  }
)

// ── Revision PIF ────────────────────────────────────────────────────────────
// A revision owns ONE PIF snapshot (gds/areas on the revision itself) or none —
// there is no PIF versioning here.

export const cloneRevisionPIF = createAsyncThunk(
  "ChangeOrderRevision/cloneRevisionPIF",
  async ({ projectId, buildingId, changeOrderId, revisionId }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.post(`${url}/${revisionId}/clone_pif`)
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to clone PIF'))
    }
  }
)

export const removeRevisionPIF = createAsyncThunk(
  "ChangeOrderRevision/removeRevisionPIF",
  async ({ projectId, buildingId, changeOrderId, revisionId }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.post(`${url}/${revisionId}/remove_pif`)
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to remove PIF'))
    }
  }
)

// data: { pif: { gds, areas } }
export const updateRevisionPIF = createAsyncThunk(
  "ChangeOrderRevision/updateRevisionPIF",
  async ({ projectId, buildingId, changeOrderId, revisionId, data }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      // Backend does params.require(:pif) — mirror the standard PIF endpoints.
      const response = await apiAxios.put(`${url}/${revisionId}/update_pif`, { pif: data }, { timeout: 120000 })
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to update PIF'))
    }
  }
)

// ── Diffs vs the parent CO# (the data this revision would override) ──────────
// 404 means there is nothing to compare against (the CO# has no PIF).

export const getRevisionPifDiff = createAsyncThunk(
  "ChangeOrderRevision/getRevisionPifDiff",
  async ({ projectId, buildingId, changeOrderId, revisionId }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.get(`${url}/${revisionId}/pif_diff`)
      return response.data
    }
    catch (error) {
      if (error.response?.status === 404) return null
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to compare PIF'))
    }
  }
)

// Field-level diff of the revision's CO form vs the CO#'s current form. Same
// { summary, changes, changed_paths } contract as getRevisionPifDiff.
export const getRevisionFormDiff = createAsyncThunk(
  "ChangeOrderRevision/getRevisionFormDiff",
  async ({ projectId, buildingId, changeOrderId, revisionId }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.get(`${url}/${revisionId}/form_diff`)
      return response.data
    }
    catch (error) {
      if (error.response?.status === 404) return null
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to compare change order form'))
    }
  }
)

// Everything the revision changes on the CO# — CO form + PIF + document intents
// — as structured data the client renders itself.
export const getRevisionChangesSummary = createAsyncThunk(
  "ChangeOrderRevision/getRevisionChangesSummary",
  async ({ projectId, buildingId, changeOrderId, revisionId }, { rejectWithValue }) => {
    try {
      const url = changeOrderRevisionUrl(projectId, buildingId, changeOrderId)
      const response = await apiAxios.get(`${url}/${revisionId}/changes_summary`)
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to load revision changes'))
    }
  }
)

const isRejectedAction = (action) => {
  return action.type.startsWith('ChangeOrderRevision/') && action.type.endsWith('rejected')
}

const handlePayload = (state, action) => {
  const { error, data, message } = action.payload

  if (error !== undefined) {
    toast.error(error);
  } else {
    const revision = data.attributes

    if (revision && state.record.id === revision.id) {
      state.record = revision
    }
    toast.success(message);
  }
}

const changeOrderRevisionSlide = createSlice({
  name: "crm_change_order_revision",
  initialState: {
    data: [],
    totalRows: 0,
    paginate: {
      totalPagesCount: 0,
      currentPage: 1,
      pageSize: 100,
      fromRecord: 0,
      toRecord: 1,
    },
    currentParams: {page: 1, per_page: 100},
    sortParams: {attribute: 'id', direction: 'desc'},
    loading: false,
    exporting: false,
    sortColumns: {},
    record: {},
    mailer: {}
  },
  reducers: {
    setCurrentPage: (state, action) => {
      state.paginate.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.paginate.pageSize = parseInt(action.payload)
    },
    setCurrentParams: (state, action) => {
      state.currentParams = action.payload
    },
    resetCurrentParams: (state) => {
      state.currentParams = {page: 1, per_page: 100}
    },
    setSortParams: (state, action) => {
      state.sortParams = action.payload
    },
    setEnterJobActiveKey: (state, action) => {
      state.enterJobActiveKey = action.payload
    },
    resetRecord: (state) => {
      state.record = {}
    },
    setNestedPage: setNestedPageHelper,
    setNestedPageSize: setNestedPageSizeHelper,
    setNestedParams: setNestedParamsHelper
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChangeOrderRevisions.pending, (state) => {
        state.loading = true
      })
      .addCase(getChangeOrderRevisions.fulfilled, (state, action) => {
        state.loading = false;
        const results = action.payload;

        state.data = objectSerializer(results.rows || []);
        state.totalRows = results.total_rows;
      })
      .addCase(getChangeOrderRevision.pending, (state) => {
        state.loading = true
      })
      .addCase(getChangeOrderRevision.fulfilled, (state, action) => {
        state.loading = false;
        state.record = action.payload;
      })
      .addCase(createChangeOrderRevision.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(updateChangeOrderRevision.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(deleteChangeOrderRevision.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(sendRevisionToCSD.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(confirmRevision.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(uploadRevisionDocument.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(removeRevisionDocument.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(markRevisionDocumentRemoval.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(unmarkRevisionDocumentRemoval.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(updateRevisionDocumentAction.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(cloneRevisionCOForm.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(cloneRevisionPIF.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(removeRevisionPIF.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(updateRevisionPIF.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addMatcher(
        isRejectedAction,
        (state, action) => {
          state.loading = false;
          toast.error(action.payload?.message || action.payload?.error || action?.error?.message)
        }
      );
  }
})

export const {
  setCurrentPage,
  setPageSize,
  setCurrentParams,
  resetCurrentParams,
  setSortParams,
  setEnterJobActiveKey,
  resetRecord,
  setNestedPage,
  setNestedPageSize,
  setNestedParams
} = changeOrderRevisionSlide.actions;

export default changeOrderRevisionSlide.reducer;
